import React from 'react';
import BadgeStatus from '../common/BadgeStatus';
import AssetRowActions from './AssetRowActions';
import { getImageUrl } from '../../utils/api';
import '../../styles/Table.css';

const AssetTable = ({
  assets,
  visibleColumns,
  pendingTransfers = {},
  onView,
  onEdit,
  onDelete,
  onCheckout,
  onCheckin,
  onRemind,
  selectedAssets,
  onSelectAsset,
  onSelectAll,
  sortBy,
  sortOrder,
  onSort,
  bulkSelectionEnabled = true,
  rowActionFlags,
}) => {
  // Helper function to render sortable header
  const renderSortableHeader = (field, label, style = {}) => {
    const isActive = sortBy === field;
    const isAsc = sortOrder === 'asc';
    
    return (
      <th
        onClick={() => onSort && onSort(field)}
        style={{
          ...style,
          textAlign: 'left',
          padding: '10px',
          fontSize: '11px',
          fontWeight: 600,
          color: '#718096',
          cursor: onSort ? 'pointer' : 'default',
          userSelect: 'none',
          position: 'relative',
          ...(isActive && { color: '#667eea' })
        }}
        title={onSort ? `Click to sort by ${label}` : ''}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>{label}</span>
          {isActive && (
            <span style={{ fontSize: '10px', color: '#667eea' }}>
              {isAsc ? '↑' : '↓'}
            </span>
          )}
          {!isActive && onSort && (
            <span style={{ fontSize: '10px', color: '#cbd5e0', opacity: 0.5 }}>
              ⇅
            </span>
          )}
        </div>
      </th>
    );
  };
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
  };

  const getConditionLabel = (condition) => {
    if (!condition) return 'N/A';
    const conditionMap = {
      'excellent': 'Excellent',
      'good': 'Good',
      'fair': 'Fair',
      'poor': 'Poor',
      'damaged': 'Damaged',
      'repair_needed': 'Repair Needed'
    };
    return conditionMap[condition] || condition;
  };

  const getConditionBadgeStyle = (condition) => {
    if (!condition) return { bg: '#e2e8f0', text: '#4a5568' };
    
    const styleMap = {
      'excellent': { bg: '#c6f6d5', text: '#22543d' },
      'good': { bg: '#bee3f8', text: '#2c5282' },
      'fair': { bg: '#fefcbf', text: '#744210' },
      'poor': { bg: '#fed7aa', text: '#7c2d12' },
      'damaged': { bg: '#fed7d7', text: '#c53030' },
      'repair_needed': { bg: '#fbb6ce', text: '#97266d' }
    };
    
    return styleMap[condition] || { bg: '#e2e8f0', text: '#4a5568' };
  };

  if (!assets || assets.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>
        <p style={{ margin: 0, fontSize: '14px' }}>No assets found</p>
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
            <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096', width: '40px' }}>
              {bulkSelectionEnabled && onSelectAll && (
                <input
                  type="checkbox"
                  checked={selectedAssets && selectedAssets.length > 0 && selectedAssets.length === assets.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onSelectAll(assets.map(a => a.id || a._id));
                    } else {
                      onSelectAll([]);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                />
              )}
            </th>
            {visibleColumns.image && (
              <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096', width: '60px' }}>
                Image
              </th>
            )}
            {visibleColumns.assetTag && renderSortableHeader('assetTag', 'Tag ID')}
            {visibleColumns.description && renderSortableHeader('description', 'Description')}
            {visibleColumns.brand && renderSortableHeader('brand', 'Brand')}
            {visibleColumns.purchaseDate && renderSortableHeader('purchaseDate', 'Purchase Date')}
            {visibleColumns.cost && renderSortableHeader('cost', 'Cost')}
            {visibleColumns.status && renderSortableHeader('status', 'Status')}
            {visibleColumns.condition && renderSortableHeader('condition', 'Condition')}
            {visibleColumns.purchasedFrom && renderSortableHeader('purchasedFrom', 'Purchased From')}
            {visibleColumns.model && renderSortableHeader('model', 'Model')}
            {visibleColumns.serialNumber && renderSortableHeader('serialNumber', 'Serial No')}
            {visibleColumns.site && (
              <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096' }}>
                Site
              </th>
            )}
            {visibleColumns.location && (
              <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096' }}>
                Location
              </th>
            )}
            {visibleColumns.category && renderSortableHeader('category', 'Category')}
            {visibleColumns.department && (
              <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096' }}>
                Department
              </th>
            )}
            {visibleColumns.assignedTo && (
              <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096' }}>
                Assigned To
              </th>
            )}
            {visibleColumns.createdAt && renderSortableHeader('createdAt', 'Date Created')}
            {visibleColumns.action && (
              <th style={{ textAlign: 'center', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096', width: '150px' }}>
                Action
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {assets.map((asset) => {
            const assetId = asset.id || asset._id;
            const isSelected = selectedAssets && selectedAssets.includes(assetId);
            return (
              <tr 
                key={assetId} 
                style={{ 
                  borderBottom: '1px solid #e2e8f0',
                  background: isSelected ? '#ebf8ff' : 'white'
                }}
              >
                <td style={{ padding: '10px' }}>
                  {bulkSelectionEnabled && onSelectAsset && (
                    <input
                      type="checkbox"
                      checked={isSelected || false}
                      onChange={(e) => {
                        if (e.target.checked) {
                          onSelectAsset(assetId, true);
                        } else {
                          onSelectAsset(assetId, false);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                </td>
                {visibleColumns.image && (
                  <td style={{ padding: '10px' }}>
                    {asset.photos && asset.photos.length > 0 && asset.photos[0].url ? (
                      <img
                        src={getImageUrl(asset.photos[0].url)}
                        alt={asset.description}
                        style={{
                          width: '40px',
                          height: '40px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          border: '1px solid #e2e8f0'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '40px',
                        height: '40px',
                        background: '#f7fafc',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#a0aec0',
                        fontSize: '20px'
                      }}>
                        📦
                      </div>
                    )}
                  </td>
                )}
                {visibleColumns.assetTag && (
                  <td style={{ padding: '10px', fontWeight: 600, color: '#2d3748', fontSize: '13px' }}>
                    {asset.assetTag || 'N/A'}
                  </td>
                )}
                {visibleColumns.description && (
                  <td style={{ padding: '10px', color: '#4a5568', fontSize: '13px' }}>
                    {asset.description || 'N/A'}
                  </td>
                )}
                {visibleColumns.brand && (
                  <td style={{ padding: '10px', color: '#718096', fontSize: '13px' }}>
                    {asset.brand || 'N/A'}
                  </td>
                )}
                {visibleColumns.purchaseDate && (
                  <td style={{ padding: '10px', color: '#718096', fontSize: '13px' }}>
                    {formatDate(asset.purchaseDate)}
                  </td>
                )}
                {visibleColumns.cost && (
                  <td style={{ padding: '10px', color: '#2d3748', fontSize: '13px', fontWeight: 500 }}>
                    {formatCurrency(asset.cost)}
                  </td>
                )}
                {visibleColumns.status && (
                  <td style={{ padding: '10px' }}>
                    <BadgeStatus status={asset.status || 'available'} />
                  </td>
                )}
                {visibleColumns.condition && (
                  <td style={{ padding: '10px' }}>
                    {asset.condition ? (
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        display: 'inline-block',
                        backgroundColor: getConditionBadgeStyle(asset.condition).bg,
                        color: getConditionBadgeStyle(asset.condition).text
                      }}>
                        {getConditionLabel(asset.condition)}
                      </span>
                    ) : (
                      <span style={{ fontSize: '13px', color: '#718096' }}>N/A</span>
                    )}
                  </td>
                )}
                {visibleColumns.purchasedFrom && (
                  <td style={{ padding: '10px', color: '#718096', fontSize: '13px' }}>
                    {asset.purchasedFrom || 'N/A'}
                  </td>
                )}
                {visibleColumns.model && (
                  <td style={{ padding: '10px', color: '#718096', fontSize: '13px' }}>
                    {asset.model || 'N/A'}
                  </td>
                )}
                {visibleColumns.serialNumber && (
                  <td style={{ padding: '10px', color: '#718096', fontSize: '13px' }}>
                    {asset.serialNumber || 'N/A'}
                  </td>
                )}
                {visibleColumns.site && (
                  <td style={{ padding: '10px', color: '#718096', fontSize: '13px' }}>
                    {asset.location?.site?.name || 'N/A'}
                  </td>
                )}
                {visibleColumns.location && (
                  <td style={{ padding: '10px', color: '#718096', fontSize: '13px' }}>
                    {asset.location?.locationDetail || 'N/A'}
                  </td>
                )}
                {visibleColumns.category && (
                  <td style={{ padding: '10px', color: '#718096', fontSize: '13px' }}>
                    {asset.category || 'N/A'}
                  </td>
                )}
                {visibleColumns.department && (
                  <td style={{ padding: '10px', color: '#718096', fontSize: '13px' }}>
                    {asset.location?.department?.name || 'N/A'}
                  </td>
                )}
                {visibleColumns.assignedTo && (
                  <td style={{ padding: '10px', color: '#2d3748', fontWeight: 500 }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span>{asset.assignedTo?.name || 'N/A'}</span>
                      {asset.status === 'in_transfer' && (() => {
                        const normalizedAssetId = assetId?.toString();
                        const transferInfo = pendingTransfers[normalizedAssetId];
                        return transferInfo?.from ? (
                          <span style={{ 
                            fontSize: '11px', 
                            color: '#718096', 
                            fontStyle: 'italic',
                            marginTop: '2px'
                          }}>
                            Transferred by: {transferInfo.from}
                          </span>
                        ) : null;
                      })()}
                    </div>
                  </td>
                )}
                {visibleColumns.createdAt && (
                  <td style={{ padding: '10px', color: '#718096', fontSize: '13px' }}>
                    {formatDate(asset.createdAt)}
                  </td>
                )}
                {visibleColumns.action && (
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <AssetRowActions
                      assetId={assetId}
                      asset={asset}
                      onView={onView}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onCheckout={onCheckout}
                      onCheckin={onCheckin}
                      onRemind={onRemind}
                      actionFlags={rowActionFlags}
                    />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default AssetTable;
